import { useEffect } from 'react';
import type { Route } from './router';

/**
 * Notifications about a screen, taken off the phone once that screen is open.
 *
 * Opening the chat used to leave every message's notification sitting in the
 * tray until it was swiped away or tapped, though there was nothing left in
 * it to read. Each push carries a tag saying what it is about (functions/src),
 * and these are the tags each screen answers.
 *
 * Each tag goes with the screen its push opens. A comment on a GiviSnap opens
 * that GiviSnap; an event's reminder opens the message it was pinned from. A
 * story post opens the GiviSnap page, where it shows, so that is the screen
 * that answers it.
 */
export const CLEARED_BY: Partial<Record<Route['name'], readonly string[]>> = {
  chat: ['chat-', 'mention-', 'voice-', 'transcode-', 'event-'],
  // The prompt, someone posting theirs or adding to their story, and the
  // streak about to run out.
  today: ['daily-', 'streak-', 'story-post-'],
  // A comment on a GiviSnap opens that GiviSnap on its comments now, not the
  // chat its old story link fell back to, so that is where it is answered.
  daily: ['reply-'],
};

type Registration = Pick<ServiceWorkerRegistration, 'getNotifications'>;

export async function clearNotifications(route: Route['name'], registration: Registration | undefined): Promise<void> {
  const prefixes = CLEARED_BY[route];
  if (!prefixes || !registration) return;
  for (const shown of await registration.getNotifications()) {
    if (prefixes.some((prefix) => shown.tag.startsWith(prefix))) shown.close();
  }
}

/**
 * On opening the screen, and again each time the app comes back to the front
 * on it -- only while it is the window being used, not merely one on screen.
 *
 * On a computer an app's window counts as visible whenever any of it is on
 * the screen, behind another window or on a second monitor. Carter kept the
 * desktop app open on the chat while working elsewhere, and each message's
 * notification showed for a moment and was gone, taken off by a chat nobody
 * was looking at. Focus is what says somebody is: on a phone the app in front
 * has it, on a computer the window being typed in does.
 */
export function useClearNotifications(route: Route['name'], enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !('serviceWorker' in navigator)) return;
    const clear = () => {
      if (document.visibilityState !== 'visible' || !document.hasFocus()) return;
      void navigator.serviceWorker
        .getRegistration()
        .then((registration) => clearNotifications(route, registration))
        .catch(() => undefined);
    };
    clear();
    document.addEventListener('visibilitychange', clear);
    // Clicked into, having been on the screen all along: what visibility misses.
    window.addEventListener('focus', clear);
    return () => {
      document.removeEventListener('visibilitychange', clear);
      window.removeEventListener('focus', clear);
    };
  }, [route, enabled]);
}
